/**
 * Duct/Pipe Accessory Tool - Accessory Placement
 *
 * Places duct accessories (dampers, filters, silencers) and pipe
 * accessories (valves, check valves, gauges). One class, two BIM
 * commands: the kind selects category, default type name and preview.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export type AccessoryKind = 'duct' | 'pipe';

export interface AccessoryGeometryData {
    position: [number, number, number];
    rotation: number;
}

export interface AccessoryElement extends Element {
    category: 'Duct Accessories' | 'Pipe Accessories';
    geometryData: AccessoryGeometryData;
}

export class AccessoryTool extends BaseTool {
    id = 'accessory';
    name = 'Accessory';
    cursor = 'crosshair';

    /** 'duct' → dampers on duct runs; 'pipe' → valves on pipe runs. */
    readonly kind: AccessoryKind;

    constructor(kind: AccessoryKind = 'duct') {
        super();
        this.kind = kind;
        this.name = kind === 'duct' ? 'Duct Accessory' : 'Pipe Accessory';
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.createAccessory(snapped);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            const snapped = this.snapToGrid(point);
            this.context.appStore.setToolPreview({
                type: this.kind === 'duct' ? 'accessory' : 'valve',
                position: snapped
            });
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private createAccessory(point: THREE.Vector3) {
        if (!this.context) return;

        const geometryData: AccessoryGeometryData = {
            position: [point.x, point.y, point.z],
            rotation: 0
        };

        const accessory: AccessoryElement = this.kind === 'duct'
            ? {
                id: uuidv4(),
                category: 'Duct Accessories',
                type: 'Balancing Damper: Rectangular',
                parameters: {
                    'Size': '300x300 mm'
                },
                constraints: [],
                geometryData
            }
            : {
                id: uuidv4(),
                category: 'Pipe Accessories',
                type: 'Gate Valve: Full Bore',
                parameters: {
                    'Size': 'DN 50',
                    'Pressure Class': 'PN 16'
                },
                constraints: [],
                geometryData
            };

        this.context.bimStore.addElement(accessory);
    }
}
